import { Skeleton } from "@timeghost/ui/shadcn/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@timeghost/ui/shadcn/tabs";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";
import ProjectDetailsLoader from "@/components/settings/projects/project-details.loader";
import ProjectHoursLoader from "@/components/settings/projects/project-hours.loader";
import ProjectReportLoader from "@/components/settings/projects/project-report.loader";
import ProjectTasksLoader from "@/components/settings/projects/project-tasks.loader";
import BoxedPage from "@/components/shell/boxed-page";

export type ProjectDetailLoaderTab = "details" | "tasks" | "hours" | "report";

const LOADER_TAB_KEYS: readonly ProjectDetailLoaderTab[] = ["details", "tasks", "hours", "report"];

interface ProjectDetailLoaderProps {
	readonly tab?: ProjectDetailLoaderTab;
	readonly tabCount?: number;
}

/** Full project detail page skeleton (header + active tab). */
export default function ProjectDetailLoader({ tab = "details", tabCount = 4 }: ProjectDetailLoaderProps): JSX.Element {
	const { t } = useTranslation();
	const tabKeys = LOADER_TAB_KEYS.slice(0, tabCount);

	return (
		<Tabs className="flex min-h-0 flex-1 flex-col gap-0" value={tab}>
			<BoxedPage
				bodyMode="fill"
				headerContent={
					<div className="flex w-full min-w-0 flex-col">
						<div className="flex min-h-12 flex-wrap items-center justify-between gap-3 px-4 py-3">
							<Skeleton aria-hidden className="h-5 w-48" />
							<Skeleton aria-hidden className="h-8 w-24 rounded-md" />
						</div>
						<TabsList className="px-4" variant="line">
							{tabKeys.map((key) => (
								<TabsTrigger disabled key={key} value={key}>
									{t(`settings.projects.tabs.${key}`)}
								</TabsTrigger>
							))}
						</TabsList>
					</div>
				}>
				{tab === "details" ? <ProjectDetailsLoader /> : null}
				{tab === "tasks" ? <ProjectTasksLoader showToolbar /> : null}
				{tab === "hours" ? <ProjectHoursLoader showToolbar /> : null}
				{tab === "report" ? <ProjectReportLoader showToolbar /> : null}
			</BoxedPage>
		</Tabs>
	);
}
